import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { denormalisedResponseEntities } from '../util/data';
import { storableError } from '../util/errors';
import { setCurrentUser } from './user.duck';

// ================ Helper Functions ================ //

export const getFavoriteIds = currentUser =>
  currentUser?.attributes?.profile?.privateData?.favorites || [];

// Builds a currentUser entity with a replaced favorites array, keeping every
// other field intact. Used for the optimistic update and the rollback.
const withFavorites = (currentUser, favorites) => ({
  ...currentUser,
  attributes: {
    ...currentUser.attributes,
    profile: {
      ...currentUser.attributes.profile,
      privateData: {
        ...currentUser.attributes.profile.privateData,
        favorites,
      },
    },
  },
});

// Serializes the profile writes. Favorites live as one whole array in
// privateData, and Sharetribe merges extended data only at the top level, so
// each updateProfile replaces the array wholesale. Two overlapping toggles
// would each read a stale array and last-write-wins would drop a favorite.
// Chaining the writes means every call reads the freshest favorites and lands
// in order, so an early response can't revert a later toggle.
// NOTE: this only serializes writes within one tab; a concurrent write from
// another tab/device is still last-write-wins (inherent to the data model).
let writeChain = Promise.resolve();

// Exposed for tests so each case starts from a clean serialization queue.
export const resetFavoritesWriteChain = () => {
  writeChain = Promise.resolve();
};

// ================ Async Thunks ================ //

//////////////////////
// Toggle Favorite //
//////////////////////

// Does NOT reuse ProfileSettingsPage.duck's updateProfileThunk (its
// include:['profileImage'] and page-state cycle are irrelevant here): it calls
// sdk.currentUser.updateProfile directly.
const toggleFavoritePayloadCreator = (listingId, { getState, dispatch, rejectWithValue, extra: sdk }) => {
  const listingIdStr = listingId.uuid || listingId;
  const currentUser = getState().user.currentUser;
  const currentFavorites = getFavoriteIds(currentUser);
  const wasFavorite = currentFavorites.includes(listingIdStr);

  // Optimistic update: the heart reflects the new state immediately. Toggle a
  // single id off the *current* list (never a captured whole-user snapshot) so
  // a sibling toggle already applied isn't discarded.
  const optimistic = wasFavorite
    ? currentFavorites.filter(id => id !== listingIdStr)
    : [listingIdStr, ...currentFavorites];
  dispatch(setCurrentUser(withFavorites(currentUser, optimistic)));

  const runWrite = () => {
    // Persist the freshest desired list (includes any toggle queued before this
    // one), so serialized writes converge instead of clobbering each other.
    const desired = getFavoriteIds(getState().user.currentUser);
    return sdk.currentUser
      .updateProfile({ privateData: { favorites: desired } }, { expand: true })
      .then(response => {
        const entities = denormalisedResponseEntities(response);
        if (entities.length !== 1) {
          throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
        }
        // Deliberately do NOT setCurrentUser(response): the response only knows
        // the favorites as of this write and would wipe a newer optimistic
        // toggle. The optimistic state is already the source of truth.
        return { listingId: listingIdStr, isFavorite: !wasFavorite };
      })
      .catch(e => {
        // Roll back just this id from the current list — never a whole-user
        // snapshot, which would wipe favorites confirmed by other toggles.
        const user = getState().user.currentUser;
        const cur = getFavoriteIds(user);
        const reverted = wasFavorite
          ? cur.includes(listingIdStr)
            ? cur
            : [listingIdStr, ...cur]
          : cur.filter(id => id !== listingIdStr);
        dispatch(setCurrentUser(withFavorites(user, reverted)));
        return rejectWithValue({ listingId: listingIdStr, error: storableError(e) });
      });
  };

  // Chain onto the serialized queue; keep the chain alive after a failure.
  const result = writeChain.then(runWrite, runWrite);
  writeChain = result.then(
    () => {},
    () => {}
  );
  return result;
};

export const toggleFavoriteThunk = createAsyncThunk(
  'favorites/toggleFavorite',
  toggleFavoritePayloadCreator
);

// Promise-returning wrapper: callers await it to know when it's safe to
// re-enable the button that triggered the toggle (rejects on failure).
export const toggleFavorite = listingId => (dispatch, getState, sdk) => {
  return dispatch(toggleFavoriteThunk(listingId)).unwrap();
};

// ================ Slice ================ //

// No slice state is needed: the favorites themselves live on currentUser, and
// each FavoriteButton tracks its own in-progress/error locally. The reducer is
// kept only so the store has a registered `favorites` slice.
const favoritesSlice = createSlice({
  name: 'favorites',
  initialState: {},
  reducers: {},
});

export default favoritesSlice.reducer;

// ================ Selectors ================ //

export const isListingFavorite = (currentUser, listingId) => {
  const listingIdStr = listingId?.uuid || listingId;
  return getFavoriteIds(currentUser).includes(listingIdStr);
};
